/** Apple Services · Distributor DEP/ADE enrollment — submission workspace (Redington live; Ingram unavailable). */
import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, Plus, RefreshCw, ScanSearch, Send, ShieldCheck, XCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import EnrollmentRequestPanel, { StatusPill } from "./EnrollmentRequestPanel";

const errMsg = (e, fb) => { const d = e.response?.data?.detail; return (typeof d === "string" ? d : d?.message) || fb; };

export const Pill = ({ ok, warn, children, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ring-1 ${
    ok ? "bg-emerald-50 text-emerald-700 ring-emerald-200" : warn ? "bg-amber-50 text-amber-800 ring-amber-200" : "bg-slate-100 text-slate-600 ring-slate-200"}`}>
    {ok ? <CheckCircle2 className="h-3 w-3" /> : warn ? <AlertTriangle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}{children}
  </span>
);

export default function DistributorEnrollmentTab() {
  const [meta, setMeta] = useState(null);
  const [requests, setRequests] = useState([]);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [showContract, setShowContract] = useState(false);

  const load = useCallback(async () => {
    try {
      const [m, r] = await Promise.all([api.get("/apple-services/enroll/meta"), api.get("/apple-services/enroll/requests")]);
      setMeta(m.data); setRequests(r.data.items || []);
    } catch (e) { toast.error(errMsg(e, "Could not load enrollment workspace")); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!meta) return <div className="text-[12px] text-slate-400 py-8" data-testid="enroll-loading">Loading…</div>;
  const c = meta.contract;
  const s = meta.settings;

  return (
    <div className="space-y-4" data-testid="enroll-tab">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-start gap-3">
          <div className="h-9 w-9 rounded-lg bg-slate-900 grid place-items-center"><Send className="h-4 w-4 text-white" /></div>
          <div className="flex-1 min-w-[220px]">
            <div className="text-[14px] font-semibold text-slate-900">Distributor Enrollment (DEP / ADE)</div>
            <div className="text-[11px] text-slate-500">Real submissions to the distributor portal · Redington only · one explicit operator confirmation per request</div>
          </div>
          <button onClick={load} data-testid="enroll-refresh" className="text-[11px] px-2.5 py-1.5 rounded-md border border-slate-200 inline-flex items-center gap-1"><RefreshCw className="h-3 w-3" /> Refresh</button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Pill ok={meta.session_active} warn={!meta.session_active} testid="enroll-pill-session">{meta.session_active ? "Redington session active" : "Redington sign-in required"}</Pill>
          <Pill ok={!!c} warn={!c} testid="enroll-pill-contract">{c ? `Form discovered ${c.observed_at?.slice(0, 16).replace("T", " ")}` : "Form contract not discovered"}</Pill>
          <Pill ok={!!c?.mapping_confirmed_at} warn={!!c && !c.mapping_confirmed_at} testid="enroll-pill-mapping">{c?.mapping_confirmed_at ? "Field mapping confirmed" : "Mapping not confirmed"}</Pill>
          <Pill ok={s.submission_enabled} warn={!s.submission_enabled} testid="enroll-pill-enabled">{s.submission_enabled ? "Submission enabled" : "Submission disabled"}</Pill>
          <Pill ok={c?.submission_tested} testid="enroll-pill-tested">{c?.submission_tested ? "Submission tested live" : "Submission not yet tested (form-derived only)"}</Pill>
          <Pill ok={s.pilot_reconciled} warn={!s.pilot_reconciled} testid="enroll-pill-pilot">{s.pilot_reconciled ? "Pilot reconciled · bulk allowed" : `Pilot pending · max ${s.max_devices_per_request} device/request`}</Pill>
          <Pill ok={meta.is_operator} warn={!meta.is_operator} testid="enroll-pill-operator">{meta.is_operator ? "You are an enrollment operator" : "You cannot submit (read-only)"}</Pill>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={() => setShowContract((v) => !v)} data-testid="enroll-toggle-contract"
            className="text-[11px] px-2.5 py-1.5 rounded-md border border-slate-200 inline-flex items-center gap-1"><ScanSearch className="h-3 w-3" /> Submission contract &amp; settings</button>
          <button onClick={() => { setCreating(true); setSelected(null); }} disabled={!meta.is_operator} data-testid="enroll-new"
            className="text-[11px] px-2.5 py-1.5 rounded-md bg-slate-900 text-white inline-flex items-center gap-1 disabled:opacity-50"><Plus className="h-3 w-3" /> New enrollment request</button>
        </div>
      </div>

      {showContract && <ContractPanel meta={meta} reload={load} />}

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="enroll-request-list">
          <div className="px-3 py-2 text-[11px] font-semibold text-slate-700 border-b border-slate-100">Requests ({requests.length})</div>
          {requests.length === 0 && <div className="text-[11px] text-slate-400 px-3 py-6 text-center" data-testid="enroll-request-empty">No enrollment requests yet.</div>}
          {requests.map((r) => (
            <button key={r.id} onClick={() => { setSelected(r.id); setCreating(false); }} data-testid={`enroll-request-row-${r.code}`}
              className={`w-full text-left px-3 py-2 border-b border-slate-100 hover:bg-slate-50 ${selected === r.id ? "bg-slate-50" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-[11px] text-slate-800">{r.code}</span>
                <StatusPill status={r.status} />
              </div>
              <div className="text-[11px] text-slate-600 truncate">{r.customer?.customer_name} · {r.customer?.apple_organization_id}</div>
              <div className="text-[10px] text-slate-400">{r.distributor} · order {r.references?.distributor_order || "—"} · {(r.devices || []).length} device(s)</div>
            </button>
          ))}
        </div>
        <div>
          {creating && <EnrollmentRequestPanel meta={meta} onDone={(id) => { setCreating(false); setSelected(id); load(); }} onCancel={() => setCreating(false)} />}
          {!creating && selected && <EnrollmentRequestPanel meta={meta} requestId={selected} onDone={() => load()} onCancel={() => setSelected(null)} />}
          {!creating && !selected && (
            <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-[12px] text-slate-400" data-testid="enroll-detail-empty">
              Select a request or start a new one. Submissions are sent only after you confirm the exact review.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ContractPanel({ meta, reload }) {
  const c = meta.contract;
  const [sample, setSample] = useState("");
  const [busy, setBusy] = useState(false);
  const [mapping, setMapping] = useState(c?.mapping || {});
  const [required, setRequired] = useState(c?.required_fields || ["distributor_order", "customer_dep_id"]);
  const [dateFmt, setDateFmt] = useState(c?.date_format || "");
  const [emails, setEmails] = useState((meta.settings.operator_emails || []).join(", "));
  useEffect(() => { setMapping(c?.mapping || {}); setRequired(c?.required_fields || ["distributor_order", "customer_dep_id"]); setDateFmt(c?.date_format || ""); }, [c]);

  const discover = async () => {
    setBusy(true);
    try { await api.post("/apple-services/enroll/form-contract/discover", { sample_distributor_order: sample || null }); toast.success("Form contract discovered (read-only)"); reload(); }
    catch (e) { toast.error(errMsg(e, "Discovery failed")); } finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true);
    try { await api.put("/apple-services/enroll/form-contract/mapping", { mapping, required_fields: required, date_format: dateFmt }); toast.success("Mapping confirmed"); reload(); }
    catch (e) { toast.error(errMsg(e, "Could not confirm mapping")); } finally { setBusy(false); }
  };
  const saveSettings = async (patch) => {
    try { await api.put("/apple-services/enroll/settings", patch); toast.success("Settings saved"); reload(); }
    catch (e) { toast.error(errMsg(e, "Could not save settings")); }
  };
  const pilotDone = async () => {
    try { await api.post("/apple-services/enroll/pilot-reconciled"); toast.success("Pilot marked reconciled"); reload(); }
    catch (e) { toast.error(errMsg(e, "Pilot not reconciled")); }
  };
  const controls = [...(c?.controls || []), ...(c?.controls_after_search || [])];

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-4" data-testid="enroll-contract-panel">
      <div>
        <div className="text-[12px] font-semibold text-slate-800">1 · Discover the live enrollment form (read-only)</div>
        <div className="text-[11px] text-slate-500 mb-2">GETs the authenticated Home.aspx and records control names, labels, buttons and hidden-state field names. Optionally searches one distributor order to observe the device grid and its checkbox names. Nothing is submitted.</div>
        <div className="flex flex-wrap gap-2">
          <input value={sample} onChange={(e) => setSample(e.target.value)} placeholder="Sample distributor order no. (optional)" data-testid="enroll-discover-sample"
            className="h-8 border border-slate-200 rounded-md px-2 text-[12px] w-72" />
          <button onClick={discover} disabled={busy || !meta.session_active} data-testid="enroll-discover-btn"
            className="text-[11px] px-3 py-1.5 rounded-md bg-slate-900 text-white inline-flex items-center gap-1 disabled:opacity-50">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <ScanSearch className="h-3 w-3" />} Discover form</button>
          {!meta.session_active && <span className="text-[11px] text-amber-700 self-center">Sign in on the Redington Connection tab first.</span>}
        </div>
        {c && (
          <div className="mt-3 text-[11px] text-slate-600 space-y-1" data-testid="enroll-contract-summary">
            <div>Controls: <b>{controls.length}</b> · Buttons: <b>{(c.buttons || []).length}</b> · Hidden state fields: <b>{(c.hidden_state_fields || []).length}</b> (names only) · Device grid on page: <b>{c.device_grid_present ? "yes" : "no"}</b></div>
            <div>Search button: <code>{c.search_button?.name || "—"}</code> = {c.search_button?.value || "—"} · Submit button: <code>{c.submit_button?.name || "—"}</code> = {c.submit_button?.value || "—"}</div>
            {c.device_grid?.observed && <div>Device grid observed via order {c.device_grid.sample_distributor_order}: headers [{(c.device_grid.headers || []).join(" · ")}] · rows {c.device_grid.rows_seen} · checkbox names e.g. <code>{(c.device_grid.checkbox_names_seen || [])[0] || "none"}</code></div>}
          </div>
        )}
      </div>

      {c && (
        <div>
          <div className="text-[12px] font-semibold text-slate-800">2 · Confirm field mapping (admin)</div>
          <div className="text-[11px] text-slate-500 mb-2">Map each logical field to the real control. Verify the meaning/format of invoice/date fields on the portal before confirming — guesses are only hints.</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {meta.logical_fields.map((lf) => (
              <label key={lf} className="text-[10px] text-slate-500">{lf}
                <div className="flex gap-2 mt-0.5">
                  <select value={mapping[lf] || ""} onChange={(e) => setMapping({ ...mapping, [lf]: e.target.value })} data-testid={`enroll-map-${lf}`}
                    className="flex-1 h-8 border border-slate-200 rounded-md px-2 text-[11px]">
                    <option value="">— not mapped —</option>
                    {controls.map((ct) => <option key={ct.name} value={ct.name}>{ct.name} · {ct.label || ct.type}{ct.logical_guess === lf ? " (guess)" : ""}</option>)}
                  </select>
                  <label className="inline-flex items-center gap-1 text-[10px]"><input type="checkbox" checked={required.includes(lf)} data-testid={`enroll-req-${lf}`}
                    onChange={(e) => setRequired(e.target.checked ? [...required, lf] : required.filter((x) => x !== lf))} /> required</label>
                </div>
              </label>
            ))}
            <label className="text-[10px] text-slate-500">invoice date format (as verified on the portal)
              <input value={dateFmt} onChange={(e) => setDateFmt(e.target.value)} placeholder="e.g. dd/MM/yyyy — leave blank if unknown" data-testid="enroll-date-format"
                className="mt-0.5 w-full h-8 border border-slate-200 rounded-md px-2 text-[11px]" /></label>
          </div>
          <button onClick={confirm} disabled={busy || !meta.is_admin} data-testid="enroll-confirm-mapping"
            className="mt-2 text-[11px] px-3 py-1.5 rounded-md bg-slate-900 text-white inline-flex items-center gap-1 disabled:opacity-50"><ShieldCheck className="h-3 w-3" /> Confirm mapping</button>
          {c.mapping_confirmed_at && <span className="ml-2 text-[11px] text-emerald-700">confirmed by {c.mapping_confirmed_by} · {c.mapping_confirmed_at.slice(0, 16).replace("T", " ")}</span>}
        </div>
      )}

      <div>
        <div className="text-[12px] font-semibold text-slate-800">3 · Submission settings (admin)</div>
        <div className="flex flex-wrap items-center gap-3 mt-2 text-[11px]">
          <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={!!meta.settings.submission_enabled} disabled={!meta.is_admin} data-testid="enroll-setting-enabled"
            onChange={(e) => saveSettings({ submission_enabled: e.target.checked })} /> Enable real submissions</label>
          <input value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="Operator e-mails (comma separated; blank = all operator roles)" disabled={!meta.is_admin} data-testid="enroll-setting-emails"
            className="h-8 border border-slate-200 rounded-md px-2 text-[11px] w-80" />
          <button onClick={() => saveSettings({ operator_emails: emails.split(",").map((x) => x.trim()).filter(Boolean) })} disabled={!meta.is_admin} data-testid="enroll-setting-save-emails"
            className="px-2.5 py-1.5 rounded-md border border-slate-200 disabled:opacity-50">Save operators</button>
          <button onClick={pilotDone} disabled={!meta.is_admin || meta.settings.pilot_reconciled} data-testid="enroll-pilot-done"
            className="px-2.5 py-1.5 rounded-md border border-slate-200 disabled:opacity-50">Mark pilot reconciled (needs a COMPLETED request)</button>
        </div>
      </div>
    </div>
  );
}
